'use client'

import React, { useState } from 'react'

interface LocationPromptModalProps {
  isOpen: boolean
  onClose: () => void
  onAllow: (coords: { lat: number; lng: number } | null) => void
}

export default function LocationPromptModal({ isOpen, onClose, onAllow }: LocationPromptModalProps) {
  const [isProcessing, setIsProcessing] = useState(false)

  if (!isOpen) return null

  const handleDeny = () => {
    onClose()
    onAllow(null) // Carga global (getClinicas)
  }

  const handleAllow = async () => {
    setIsProcessing(true)
    
    // Tenta usar API nativa (HTTPS/Localhost)
    if (navigator.geolocation) {
      try {
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(
            (pos) => resolve(pos),
            (err) => reject(err),
            { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
          )
        })
        onAllow({ lat: position.coords.latitude, lng: position.coords.longitude })
        onClose()
        return
      } catch (error) {
        console.warn('Geolocation nativa falhou. Tentando fallback reverso por IP...', error)
      }
    }

    // Fallback: Se estiver no ambiente local HTTP (scheduling.sstlocal) a nativa bloqueia. Usamos IP-API.
    try {
      const response = await fetch('https://ipapi.co/json/')
      const data = await response.json()
      if (data && data.latitude && data.longitude) {
        onAllow({ lat: parseFloat(data.latitude), lng: parseFloat(data.longitude) })
        onClose()
        return
      }
    } catch (ipError) {
      console.error('Fallback IP-API falhou:', ipError)
    }

    // Se tudo falhar
    onAllow(null)
    onClose()
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/10 backdrop-blur-md transition-opacity">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden transform transition-all scale-100">
        <div className="p-6 md:p-8">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
            <svg className="h-8 w-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          
          <h3 className="text-2xl font-bold text-center text-gray-900 mb-2">
            Permitir Localização?
          </h3>
          <p className="text-center text-gray-600 mb-8">
            Para exibir as clínicas mais <strong>próximas de você</strong> primeiro, precisamos de acesso à sua localização aproximada.
          </p>
          
          <div className="flex flex-col space-y-3">
            <button
              onClick={handleAllow}
              disabled={isProcessing}
              className="w-full flex justify-center items-center py-3.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-bold text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors disabled:opacity-70"
            >
              {isProcessing ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Buscando Localização...
                </span>
              ) : (
                'Permitir e Buscar'
              )}
            </button>
            
            <button
              onClick={handleDeny}
              disabled={isProcessing}
              className="w-full py-3.5 px-4 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors font-semibold text-sm border border-gray-200"
            >
              Não mostrar por proximidade
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
